<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="warehouse-management-tab" :class="{ 'form-disabled': disabled }">
    <div class="row">
      <!-- Warehouse (FORM02: warehouse_management.cat_emcr_warehouses). Warehouse Number unik antar baris (row_checks) dan
           tiap nilainya punya baris Storage Type (sync_rows) -- dibuat/dihapus otomatis oleh kerangka bersama. -->
      <template v-for="table in TABLES" :key="table.field">
      <div v-if="isTableVisible(WAREHOUSE_TAB, table.field)" class="col-md-6">
        <div class="card h-100">
          <div class="card-header py-3 pb-2 d-flex align-items-center justify-content-between" style="padding-left: 1rem">
            <h6 class="mb-0 fw-semibold">
              {{ table.title }} <span v-if="isTableMandatory(WAREHOUSE_TAB, table.field)" class="exclamation-mark-error-tab">*</span>
            </h6>
            <ButtonPrime v-if="canAddRow(WAREHOUSE_TAB, table.field)" icon="pi pi-plus" label="Add" size="small" :disabled="disabled" @click="addRow(WAREHOUSE_TAB, table.field)" />
          </div>
          <div class="card-body pt-0 px-0 card-body-tab">
            <DataTable :value="dialog.form[table.field] || []" class="p-datatable-sm p-datatable-gridlines" scrollable scroll-height="420px">
              <template v-for="col in table.columns" :key="col.column">
                <Column v-if="isColumnVisibleFn(col.column, table.field, WAREHOUSE_TAB)" header-class="bg-light text-center" :style="columnWidth(col.width)">
                  <template #header>
                    {{ col.label }} <span v-if="isColumnMandatoryInRows(WAREHOUSE_TAB, table.field, col.column)" class="exclamation-mark-error-tab">*</span>
                  </template>
                  <template #body="{ data }">
                    <SelectPrime show-clear v-model="data[col.column]" size="small" fluid :filter="col.filter"
                      :options="columnSelectOptions(WAREHOUSE_TAB, table.field, col.column, data)"
                      option-label="label" option-value="value" :placeholder="`Select ${col.label}`"
                      :loading="dialog.loading[columnOptionsKey(WAREHOUSE_TAB, table.field, col.column, data)]"
                      :invalid="isColumnInvalidFn(data, col.column)"
                      :disabled="disabled || !isColumnEnabledFn(col.column, table.field, WAREHOUSE_TAB) || !isColumnDependencyReadyFn(col.column, table.field, WAREHOUSE_TAB, data)"
                      @show="loadColumnOptions(WAREHOUSE_TAB, table.field, col.column, data)"
                      @change="onColumnChange(WAREHOUSE_TAB, table.field, col.column, data)"
                    >
                      <template #value="{ value, placeholder }">
                        <span v-if="value != null && value !== ''">{{ getSelectValueLabelFn(dialog.options[columnOptionsKey(WAREHOUSE_TAB, table.field, col.column, data)], value) }}</span>
                        <span v-else>{{ placeholder }}</span>
                      </template>
                    </SelectPrime>
                  </template>
                </Column>
              </template>
              <Column v-if="canRemoveRow(WAREHOUSE_TAB, table.field)" header-class="bg-light text-center" style="width: 56px">
                <template #header>Action</template>
                <template #body="{ index }">
                  <ButtonPrime icon="pi pi-trash" severity="danger" text size="small" :disabled="disabled" @click="removeRow(table.field, index)" />
                </template>
              </Column>
              <template #empty>
                <div class="text-muted text-center small py-3">{{ table.empty }}</div>
              </template>
            </DataTable>
          </div>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { cataloguerTabMixin, useCataloguerTabEngine, createTabDialog } from "./New_CataloguerTab";

// Key tab & field repeatable di FORM02_BASE.json
const WAREHOUSE_TAB = "warehouse_management";
const WAREHOUSES_FIELD = "cat_emcr_warehouses";
const STORAGE_TYPES_FIELD = "cat_emcr_storage_types";

// Layout tabel & kolom (urutan, label, lebar) -- hardcode di Vue sesuai prinsip "layout hardcode, behavior dynamic".
// Tampil/wajib/opsi tiap kolom, keunikan Warehouse Number & sinkron baris Storage Type dari config FORM02.
// Plant (Warehouse) = plant di tabel Plant; Plant & Warehouse Number (Storage Type) = nilai di tabel Warehouse.
const TABLES = [
  {
    field: WAREHOUSES_FIELD,
    title: "Warehouse Management 1",
    empty: "No warehouse data yet.",
    columns: [
      { column: "plant", label: "Plant", width: 150 },
      { column: "warehouse_number", label: "Warehouse Number", width: 220, filter: true },
      { column: "stock_removal", label: "Stock Removal", width: 150 },
      { column: "stock_replacement", label: "Stock Replacement", width: 160 },
      { column: "storage_section_ind", label: "Storage Section Indicator", width: 220, filter: true }
    ]
  },
  {
    field: STORAGE_TYPES_FIELD,
    title: "Warehouse Management 2",
    empty: "Storage type rows follow the Warehouse Number in Warehouse Management 1.",
    columns: [
      { column: "plant", label: "Plant", width: 150 },
      { column: "warehouse_number", label: "Warehouse Number", width: 180 },
      { column: "storage_type", label: "Storage Type", width: 220, filter: true }
    ]
  }
];

// Props, setup engine, dialog, bind ke mcrItem, addRow/removeRow/opsi kolom/sync_rows: kerangka bersama New_CataloguerTab.js.
export default {
  name: "WarehouseManagementTab",
  mixins: [cataloguerTabMixin],
  setup(props) {
    return useCataloguerTabEngine(props, (vm) => ({
      // data_source detail_array dengan source "form" (opsi Plant/Warehouse Number dari tabel lain) dibaca dari item ini.
      getDetailSources: () => ({ form: vm.dialog.form })
    }));
  },
  data() {
    return {
      WAREHOUSE_TAB,
      TABLES,
      dialog: createTabDialog(this.mcrItem)
    };
  },
  methods: {
    columnWidth(width) {
      return { width: `${width}px`, minWidth: `${width}px` };
    },
    // Hook kerangka bersama, dipanggil tiap item dipilih/berganti. Baris yang diatur `sync_rows` (INALUM: satu baris Warehouse
    // per plant; semua: satu baris Storage Type per Warehouse Number) dirapikan dulu -- kecuali mode lihat-saja.
    onItemBound() {
      this.ensureTableArrays(WAREHOUSE_TAB);
      if (!this.disabled) this.syncAllLinkedRows();
      // Default per plant (INALUM WAREHOUSE_CONFIG, `row_autofill`): cuma kolom yang masih kosong di baris yang sudah ada.
      this.applyRowAutomationOnOpen(WAREHOUSE_TAB);
      this.preloadTabLabels(WAREHOUSE_TAB);
    }
  }
};
</script>
